<template>
    <div class="academic-course-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && academicCourse.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="academicCourse"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            v-if="editMode"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="academicCourse"
                            @meta-change="onMetaChange"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import AddEditForm from '@/components/academic-courses/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Academic Course'
    },

    validations: {
        academicCourse: {
            name: {
                required
            },
            imageId: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit academic course' : 'Add academic course',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Academic Courses',
                    to: '/academic-courses'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            academicCourse: {},
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.academicCourse = await this.getAcademicCourse(this.id);

                this.academicCourse.author = this.academicCourse.creator;
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addAcademicCourse: 'academicCourses/store',
            deleteAcademicCourse: 'academicCourses/destroy',
            getAcademicCourse: 'academicCourses/show',
            updateAcademicCourse: 'academicCourses/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addAcademicCourse(
                        this.academicCourse
                    );

                    this.$router.push(`/academic-courses/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateAcademicCourse({
                        id: this.id,
                        data: this.academicCourse
                    });

                    this.academicCourse.updatedAt = updatedAt;

                    this.$toaster('Academic course updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteAcademicCourse(this.id);

                this.$router.push('/academic-courses');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onMetaChange(meta) {
            this.academicCourse.meta = { ...meta };
        }
    }
};
</script>
